import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { 
  PlusIcon, 
  TruckIcon, 
  MagnifyingGlassIcon,
  WrenchScrewdriverIcon,
  CalendarDaysIcon,
  PencilIcon,
  TrashIcon,
  DocumentArrowUpIcon
} from '@heroicons/react/24/outline';
import { formatDistanceToNow, formatDate, cn } from '../utils';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import CarForm from '../components/CarForm';
import ExcelImport from '../components/ExcelImport';

// Minimalist skeleton loader with taller image area
const CarSkeleton = () => (
  <div className="bg-white/5 rounded-3xl p-6 space-y-4 animate-pulse backdrop-blur-sm border border-white/10">
    <div className="h-48 bg-white/10 rounded-2xl"></div>
    <div className="space-y-3">
      <div className="h-6 bg-white/10 rounded-lg w-3/4"></div>
      <div className="h-4 bg-white/10 rounded-lg w-1/2"></div>
      <div className="space-y-2">
        <div className="h-3 bg-white/10 rounded w-full"></div>
        <div className="h-3 bg-white/10 rounded w-4/5"></div>
      </div>
    </div>
  </div>
);

// Clean loading spinner
const LoadingSpinner = () => (
  <div className="flex flex-col items-center justify-center py-20">
    <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
    <p className="text-white/60 mt-4 text-sm">Loading...</p>
  </div>
);

const Cars = () => {
  const { isAdmin } = useAuth();
  const { t } = useTranslation();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [totalPages, setTotalPages] = useState(1);
  const [isCarFormOpen, setIsCarFormOpen] = useState(false);
  const [selectedCar, setSelectedCar] = useState(null);
  const [isExcelImportOpen, setIsExcelImportOpen] = useState(false);

  useEffect(() => {
    fetchCars();
  }, [filters, pagination]);

  const fetchCars = async () => {
    setLoading(true);
    try {
      const response = await apiService.getCars({ ...filters, ...pagination });
      if (response.success && response.cars) {
        setCars(response.cars);
        setTotalPages(response.pagination?.pages || 1);
      }
    } catch (error) {
      toast.error(error.message || t('cars.messages.failedToFetch', { defaultValue: 'Failed to fetch cars' }));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (search) => {
    setFilters(prev => ({
      ...prev,
      search: search || undefined
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleOpenCarForm = (car = null) => {
    setSelectedCar(car);
    setIsCarFormOpen(true);
  };

  const handleCloseCarForm = () => {
    setSelectedCar(null);
    setIsCarFormOpen(false);
  };

  const handleCarSaved = (savedCar) => {
    if (selectedCar) {
      setCars(prev => prev.map(car => 
        car._id === savedCar._id ? savedCar : car
      ));
    } else {
      setCars(prev => [savedCar, ...prev]);
    }
    handleCloseCarForm();
  };

  const handleImportComplete = () => {
    fetchCars();
  };

  const handleDeleteCar = async (carId, carNumberPlate) => {
    if (!window.confirm(t('cars.confirmDelete', { numberPlate: carNumberPlate }))) {
      return;
    }

    try {
      await apiService.deleteCar(carId);
      setCars(prev => prev.filter(car => car._id !== carId));
      toast.success(t('cars.messages.deleteSuccess'));
    } catch (error) {
      toast.error(error.message || t('cars.messages.deleteFailed', { defaultValue: 'Failed to delete car' }));
    }
  };

  const getMaintenanceStatus = (car) => {
    const now = new Date();
    const lastService = car.lastServiceDate ? new Date(car.lastServiceDate) : null;
    const nextService = car.nextServiceDate ? new Date(car.nextServiceDate) : null;

    if (nextService && now > nextService) {
      return { status: 'overdue', color: 'bg-red-500/20 text-red-300 border-red-500/30', text: t('cars.maintenance.overdue') };
    } else if (nextService && (nextService.getTime() - now.getTime()) / (1000 * 60 * 60 * 24) <= 7) {
      return { status: 'due', color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30', text: t('cars.maintenance.dueSoon') };
    } else if (lastService) {
      return { status: 'current', color: 'bg-green-500/20 text-green-300 border-green-500/30', text: t('cars.maintenance.current') };
    } else {
      return { status: 'unknown', color: 'bg-gray-500/20 text-gray-300 border-gray-500/30', text: t('cars.maintenance.unknown') };
    }
  };

  return (
    <div className="min-h-screen p-6 space-y-8">
      {/* Minimalist Header */}
      <div className="text-center space-y-2">
        <h1 className="text-4xl font-light text-white tracking-wide">
          {t('cars.title')}
        </h1>
        <p className="text-white/60 text-sm font-light">
          Fleet Management System
        </p>
      </div>

      {/* Action Buttons */}
      {isAdmin && (
        <div className="flex justify-center gap-4">
          <button
            onClick={() => setIsExcelImportOpen(true)}
            className="group flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 rounded-2xl text-white/80 hover:text-white transition-all duration-300 hover:scale-105"
          >
            <DocumentArrowUpIcon className="h-4 w-4 group-hover:scale-110 transition-transform" />
            <span className="text-sm font-light">{t('cars.excelImport')}</span>
          </button>
          <button
            onClick={() => handleOpenCarForm()}
            className="group flex items-center gap-2 px-6 py-3 bg-white hover:bg-white/90 text-dark-900 rounded-2xl transition-all duration-300 hover:scale-105 shadow-lg"
          >
            <PlusIcon className="h-4 w-4 group-hover:rotate-90 transition-transform" />
            <span className="text-sm font-medium">{t('cars.addCar')}</span>
          </button>
        </div>
      )}

      {/* Minimalist Search */}
      <div className="max-w-md mx-auto">
        <div className="relative group">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-white/40 group-focus-within:text-white/60 transition-colors" />
          <input
            type="text"
            placeholder={t('cars.searchPlaceholder')}
            className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:bg-white/20 focus:border-white/40 transition-all duration-300"
            value={filters.search || ''}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
      </div>

      {/* 3D Cars Grid with proper overflow handling */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-12 max-w-7xl mx-auto px-4" style={{ perspective: '1200px' }}>
        {loading ? (
          Array.from({ length: 8 }, (_, i) => (
            <div key={i} style={{ animationDelay: `${i * 50}ms` }} className="animate-fade-up">
              <CarSkeleton />
            </div>
          ))
        ) : cars.length === 0 ? (
          <div className="col-span-full text-center py-20">
            <TruckIcon className="h-16 w-16 text-white/30 mx-auto mb-4" />
            <h3 className="text-xl font-light text-white mb-2">No vehicles found</h3>
            <p className="text-white/60 text-sm">
              {filters.search ? 'Try different search terms' : 'Add your first vehicle to get started'}
            </p>
          </div>
        ) : (
          cars.map((car, index) => {
            const maintenanceStatus = getMaintenanceStatus(car);
            const primaryImage = car.images?.find(img => img.isPrimary) || car.images?.[0];
            
            return (
              <div
                key={car._id}
                className="group animate-fade-up"
                style={{ 
                  animationDelay: `${index * 50}ms`,
                  transform: 'perspective(1000px)',
                  transformStyle: 'preserve-3d'
                }}
              >
                {/* 3D Card Container */}
                <div className="relative transform-gpu transition-all duration-500 hover:scale-105 hover:-translate-y-2" style={{ transformStyle: 'preserve-3d' }}>
                  {/* 3D Shadow */}
                  <div className="absolute inset-0 bg-black/20 rounded-3xl transform translate-y-4 scale-95 blur-xl opacity-60 group-hover:opacity-80 group-hover:scale-100 transition-all duration-500"></div>
                  
                  {/* Main Card */}
                  <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl overflow-visible transform-gpu transition-all duration-500 group-hover:bg-white/15 group-hover:border-white/30" style={{ transformStyle: 'preserve-3d' }}>
                    
                    {/* Car Image with 3D popup effect - Full image display */}
                    <div className="relative h-56 overflow-visible bg-white/5 rounded-t-3xl">
                      {primaryImage ? (
                        <div className="relative w-full h-full">
                          <img
                            src={primaryImage.url}
                            alt={`${car.make} ${car.model}`}
                            className="w-full h-full object-contain rounded-t-3xl transition-all duration-700 transform-gpu group-hover:scale-125 group-hover:translate-z-20 group-hover:shadow-2xl group-hover:rounded-2xl group-hover:-translate-y-4 relative z-10"
                            style={{
                              transformStyle: 'preserve-3d',
                            }}
                          />
                          {/* 3D Image shadow */}
                          <div className="absolute inset-0 bg-black/20 rounded-t-3xl transform translate-y-2 scale-95 blur-lg opacity-0 group-hover:opacity-100 group-hover:translate-y-6 group-hover:scale-105 transition-all duration-700 z-0"></div>
                        </div>
                      ) : (
                        <div className="w-full h-full bg-white/5 flex items-center justify-center rounded-t-3xl group-hover:scale-125 group-hover:-translate-y-4 transition-all duration-700 transform-gpu">
                          <TruckIcon className="h-16 w-16 text-white/30" />
                        </div>
                      )}
                      
                      {/* Status Badge */}
                      <div className="absolute top-4 right-4 z-20">
                        <span className={cn(
                          'px-3 py-1 text-xs font-medium rounded-full border backdrop-blur-sm transition-all duration-500 group-hover:scale-110 group-hover:-translate-y-1',
                          maintenanceStatus.color
                        )}>
                          {maintenanceStatus.text}
                        </span>
                      </div>
                    </div>

                    {/* Card Content */}
                    <div className="card-content p-6 space-y-4 transform-gpu transition-all duration-500"  style={{ transformStyle: 'preserve-3d' }}>
                      
                      {/* Car Title */}
                      <div className="space-y-1">
                        <h3 className="text-xl font-medium text-white group-hover:text-primary-300 transition-colors">
                          {car.numberPlate}
                        </h3>
                        <p className="text-white/60 text-sm font-light">
                          {car.make} {car.model} • {car.year}
                        </p>
                      </div>

                      {/* Key Info */}
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-white/50">Owner</span>
                          <span className="text-white/80 font-medium">{car.owner.name}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-white/50">Mileage</span>
                          <span className="text-white/80 font-medium">{car.mileage?.toLocaleString() || 0} km</span>
                        </div>
                        {car.lastServiceDate && (
                          <div className="flex justify-between items-center">
                            <span className="text-white/50">Last Service</span>
                            <span className="text-white/80 font-medium">{formatDate(car.lastServiceDate)}</span>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      {isAdmin && (
                        <div className="flex items-center justify-between pt-4 border-t border-white/10">
                          <Link
                            to={`/cars/${car._id}`}
                            className="text-white/60 hover:text-white text-sm font-medium transition-colors"
                          >
                            View Details
                          </Link>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                handleOpenCarForm(car);
                              }}
                              className="p-2 text-white/50 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-300"
                              title={t('cars.editCar')}
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                handleDeleteCar(car._id, car.numberPlate);
                              }}
                              className="p-2 text-white/50 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-all duration-300"
                              title={t('cars.deleteCar')}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Minimalist Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-8 pt-8">
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
            disabled={pagination.page === 1}
            className="px-6 py-3 bg-white/10 hover:bg-white/20 disabled:bg-white/5 border border-white/20 disabled:border-white/10 text-white/80 disabled:text-white/40 rounded-2xl transition-all duration-300 hover:scale-105 disabled:hover:scale-100 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          <span className="text-white/60 text-sm font-light">
            Page {pagination.page} of {totalPages}
          </span>
          
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: Math.min(totalPages, prev.page + 1) }))}
            disabled={pagination.page === totalPages}
            className="px-6 py-3 bg-white/10 hover:bg-white/20 disabled:bg-white/5 border border-white/20 disabled:border-white/10 text-white/80 disabled:text-white/40 rounded-2xl transition-all duration-300 hover:scale-105 disabled:hover:scale-100 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {/* Modals */}
      <CarForm
        isOpen={isCarFormOpen}
        onClose={handleCloseCarForm}
        car={selectedCar}
        onCarSaved={handleCarSaved}
      />

      <ExcelImport
        isOpen={isExcelImportOpen}
        onClose={() => setIsExcelImportOpen(false)}
        onImportComplete={handleImportComplete}
      />

      <style jsx>{`
        @keyframes fade-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-up {
          animation: fade-up 0.6s ease-out forwards;
        }

        /* 3D hover effects */
        .group:hover .transform-gpu {
          transform: perspective(1000px) rotateX(5deg) rotateY(-5deg) translateZ(20px);
        }

        /* Enhanced 3D image popup effect with zoom */
        .group:hover img {
          transform: perspective(800px) translateZ(40px) scale(1.25) translateY(-16px) rotateX(10deg) !important;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.8) !important;
          z-index: 50 !important;
          position: relative !important;
        }

        /* Additional 3D depth for card content */
        .group:hover .card-content {
          transform: perspective(800px) translateZ(10px);
        }
      `}</style>
    </div>
  );
};

export default Cars;